import React from "react";
import { Link } from "react-router-dom";
import { STORYBOARD_TEMPLATES } from "../constants/storyboardTemplates";

// Copy for the storyboard template pages (/storyboard-templates/:slug). Each
// page is written for one real use case, not a ratio swapped into
// boilerplate, so it earns its place in search on its own. The board each
// template opens lives in constants/storyboardTemplates.js.

const G = (slug, children) => (
  <Link to={`/guides/${slug}`} style={{ color: "#00a4e4" }}>{children}</Link>
);
const T = (slug, children) => (
  <Link to={`/storyboard-templates/${slug}`} style={{ color: "#00a4e4" }}>{children}</Link>
);

const COPY = {
  "16-9-widescreen": {
    title: "16:9 Storyboard Template",
    metaTitle: "Free 16:9 Storyboard Template — Printable PDF & Online | Striply",
    description:
      "A free 16:9 storyboard template with six widescreen frames per page and space for shot type, camera move, duration, action, and dialogue. Fill it in online or print a blank PDF.",
    summary: "Six HD frames per landscape page — the all-purpose board for YouTube, short films, and explainers.",
    related: ["2-39-cinemascope", "vertical-9-16"],
    guides: ["what-is-a-storyboard", "camera-shot-types"],
    body: (
      <>
        <p>
          16:9 is the shape of almost every screen people watch video on: TVs,
          laptops, YouTube, and streaming services. If you are planning a YouTube
          video, a short film, a corporate piece, a tutorial, or an explainer,
          this is the template to start from. Each US Letter landscape page holds
          six 16:9 frames in a 3 × 2 grid, which is enough to see a whole short
          scene at once while leaving room to draw.
        </p>

        <h2>What's on the page</h2>
        <p>
          Under every frame is a notes box with the shot number, a shot-type and
          camera-move field, a duration in seconds, and two writing areas: one
          for action and one for dialogue or sound. Shot numbers continue across
          pages, so shot 7 is always the first frame of page two. If you need
          more room to write, switch the board to <strong>3 frames, notes
          beside</strong>; for a quick first pass, switch to <strong>9
          thumbnails</strong>.
        </p>

        <h2>Tips for 16:9 boards</h2>
        <ul>
          <li>
            <strong>Use the width.</strong> 16:9 rewards horizontal staging:
            put two characters on opposite thirds of the frame instead of stacking
            them in the middle.
          </li>
          <li>
            <strong>Plan for vertical cut-downs.</strong> If the video will also
            be posted as a Reel or Short, set the Matte to the{" "}
            <strong>9:16 crop guide</strong> and keep key action inside the
            unshaded middle band. Our guide to{" "}
            {G("storyboard-aspect-ratios", "aspect ratios and crop guides")}{" "}
            explains why the middle third matters.
          </li>
          <li>
            <strong>Keep text inside title safe.</strong> Turn on safe areas to
            see where lower thirds and captions will sit comfortably.
          </li>
          <li>
            <strong>Note your durations.</strong> Adding up seconds per shot is
            the quickest way to see whether a 60-second video is really 90.
          </li>
        </ul>
        <p>
          Working in widescreen film instead? Try the{" "}
          {T("2-39-cinemascope", "2.39:1 CinemaScope template")}.
        </p>
      </>
    ),
  },

  "2-39-cinemascope": {
    title: "2.39:1 CinemaScope Storyboard Template",
    metaTitle: "Free 2.39:1 Widescreen Storyboard Template (CinemaScope) | Striply",
    description:
      "A free 2.39:1 anamorphic storyboard template: three wide CinemaScope frames per page with notes beside each shot. Ideal for feature films and music videos. Edit online or print.",
    summary: "Three wide scope frames per page with roomy notes beside each — for features and music videos.",
    related: ["16-9-widescreen", "animation"],
    guides: ["storyboard-aspect-ratios", "camera-shot-types"],
    body: (
      <>
        <p>
          2.39:1 — usually called scope, CinemaScope, or anamorphic, and often
          rounded to 2.35 or 2.40 — is the widest common film format. It is the
          look of epic landscapes, tense standoffs with characters at opposite
          edges of the frame, and most music videos that want to feel cinematic.
          Because the frames are so wide and short, this template stacks three
          per page and puts the notes <em>beside</em> each frame instead of
          underneath, so you get large drawings and plenty of writing space.
        </p>

        <h2>Frame it in scope, or letterbox it in 16:9</h2>
        <p>
          There are two ways to board widescreen. This template draws the frames
          at 2.39:1 directly, which gives you the most drawing area. If your final
          file will be a 16:9 video with a scope picture inside it — common for
          online releases and music videos — switch the Frame to 16:9 and set the
          Matte to <strong>2.39:1 letterbox bars</strong>. Your boards will then
          show the black bars exactly as viewers will see them. The{" "}
          {G("storyboard-aspect-ratios", "aspect ratio guide")} covers the
          trade-offs.
        </p>

        <h2>Composing for scope</h2>
        <ul>
          <li>
            <strong>Fill the sides with meaning.</strong> In scope, an empty side
            of the frame reads as deliberate negative space: isolation, distance,
            something about to arrive.
          </li>
          <li>
            <strong>Close-ups get wider.</strong> A close-up in 2.39:1 shows more
            background beside the face than in 16:9. Decide what that background
            says.
          </li>
          <li>
            <strong>Mind screen direction.</strong> Wide frames make eyelines and
            left-to-right movement very obvious; keep them consistent across cuts.
          </li>
        </ul>
        <p>
          New to shot vocabulary? Read{" "}
          {G("camera-shot-types", "camera shot types for storyboards")}. Making
          something for TV or YouTube instead? Use the{" "}
          {T("16-9-widescreen", "16:9 template")}.
        </p>
      </>
    ),
  },

  "vertical-9-16": {
    title: "Vertical 9:16 Storyboard Template for Reels, TikTok & Shorts",
    metaTitle: "Free 9:16 Vertical Storyboard Template — Reels, TikTok, Shorts | Striply",
    description:
      "A free vertical 9:16 storyboard template for Instagram Reels, TikTok, and YouTube Shorts: four tall frames per page with shot notes and timing. Plan your hook, edit online, or print.",
    summary: "Four tall 9:16 frames per page with timing notes — for Reels, TikTok, Shorts, and Stories.",
    related: ["16-9-widescreen", "animation"],
    guides: ["storyboard-aspect-ratios", "what-is-a-storyboard"],
    body: (
      <>
        <p>
          Vertical video is its own medium. A 9:16 frame is tall and narrow, and
          viewers decide in the first second or two whether to keep watching.
          Storyboarding a short-form video — even one you will shoot on a phone —
          is the fastest way to tighten the hook, cut dead time, and make sure
          every shot reads on a small screen. This template puts four 9:16 frames
          side by side on a landscape page, with a tall notes box under each for
          action, on-screen text, and audio.
        </p>

        <h2>Plan the first three seconds</h2>
        <p>
          Use shot 1 for your hook: the most surprising image, the result before
          the process, or a question on screen. Give it a duration of a second or
          two in the notes, and check that the next shot changes something —
          framing, subject, or movement — so the video keeps moving. Adding up the
          durations across the board tells you the real running time.
        </p>

        <h2>Tips for vertical frames</h2>
        <ul>
          <li>
            <strong>Stack, don't spread.</strong> Vertical frames favor subjects
            arranged top to bottom: a face over a product, a person over a
            location.
          </li>
          <li>
            <strong>Leave room for the app.</strong> Reels, TikTok, and Shorts
            overlay captions, usernames, and buttons along the bottom and right
            side of the screen. Keep faces and on-screen text toward the upper
            middle of the frame, and turn on safe areas as a reminder to stay
            away from the edges.
          </li>
          <li>
            <strong>Write the on-screen text.</strong> Most short-form video is
            watched without sound, so note captions and text overlays in the
            dialogue box alongside any voice-over.
          </li>
          <li>
            <strong>Shooting horizontal too?</strong> Board in 16:9 with the{" "}
            <strong>9:16 crop guide</strong> instead, so one shoot serves both
            versions. See the {T("16-9-widescreen", "16:9 template")} and the{" "}
            {G("storyboard-aspect-ratios", "guide to crop guides")}.
          </li>
        </ul>
      </>
    ),
  },

  animation: {
    title: "Animation Storyboard Template",
    metaTitle: "Free Animation Storyboard Template — 16:9 with Safe Areas | Striply",
    description:
      "A free animation storyboard template: three 16:9 frames per portrait page, notes beside each panel for action, dialogue, and timing, and broadcast safe areas built in. Edit online or print.",
    summary: "Three 16:9 panels per portrait page, notes beside each, safe areas on — for 2D and 3D animation.",
    related: ["16-9-widescreen", "2-39-cinemascope"],
    guides: ["what-is-a-storyboard", "camera-shot-types"],
    body: (
      <>
        <p>
          In animation, the storyboard is where the film is actually directed.
          Every pose, cut, and camera move is decided on the board before anyone
          animates a frame, because changing it later is expensive. Animation
          boards are usually drawn several panels per page, read top to bottom,
          with notes beside each panel for action, dialogue, and timing. This
          template follows that convention: three 16:9 panels on a portrait US
          Letter page, notes to the right, and action-safe and title-safe areas
          switched on.
        </p>

        <h2>How to use it</h2>
        <ul>
          <li>
            <strong>One panel per key pose or change.</strong> A single shot in
            animation often needs several panels: the start of an action, the key
            pose, and the end. They can all share a shot in your notes.
          </li>
          <li>
            <strong>Time everything.</strong> Use the duration field on each
            panel. Animators work from these timings, and the totals show whether
            a sequence fits its slot.
          </li>
          <li>
            <strong>Draw camera moves on the panel.</strong> Mark pans and push-ins
            with arrows and an end frame, then pick the move in the panel header so
            it reads clearly in the notes. Our{" "}
            {G("camera-shot-types", "guide to shot types and camera moves")}{" "}
            covers the vocabulary.
          </li>
          <li>
            <strong>Respect safe areas.</strong> Broadcast and streaming
            deliveries crop and overlay near the edges; keep key action inside
            action safe and any text inside title safe.
          </li>
        </ul>

        <h2>From boards to animatic</h2>
        <p>
          Once the board is approved, the panels are usually cut together with the
          dialogue track into an animatic to check the timing. Keeping accurate
          durations on the board makes that step much faster. For the bigger
          picture, read {G("what-is-a-storyboard", "what a storyboard is and how to make one")}.
          Working on a widescreen short instead? Try the{" "}
          {T("2-39-cinemascope", "2.39:1 template")}.
        </p>
      </>
    ),
  },
};

export const STORYBOARD_TEMPLATE_PAGES = STORYBOARD_TEMPLATES.map((template) => ({
  ...template,
  ...COPY[template.slug],
}));

export const STORYBOARD_TEMPLATE_PAGES_BY_SLUG = Object.fromEntries(
  STORYBOARD_TEMPLATE_PAGES.map((t) => [t.slug, t])
);
